import { Col, Modal, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { amlzC2osobyDetailGet, amlzC2osobyDetailsend, platebniInstrukcePDFapi } from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';
import {
    ISubjPravo,
    biAmlzRerouteApi,
    osobyC2Nova,
    pdfBiRerouteApi,
    pdfPodpisApi,
    zadostPravoPUT
} from 'services/spravaEmitenta/zadostPravo';

import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

import AgendaAmlz from './formular/AgendaAmlzFormular';

const OsobyZastupujiciEmitenta: React.FC = () => {
    const { confirm } = Modal;
    const { navigateWorkaround } = useNavigateWorkaround();
    const { aktivniFirma, zadostAgenda, NotificationComponent, language, profily } = useContext(Context);
    const [showInstrukceAfterFinish, setshowInstrukceAfterFinish] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [zadostData, setZadostData] = useState<ISubjPravo>();
    const [submitZadostData, setSubmitZadostData] = useState<ISubjPravo>();
    const [putResp, setPutResp] = useState<string>('');
    const [isSubmittingManually, setIsSubmittingManually] = useState<boolean>(false);
    const [personDataFinish, setPersonDataFinish] = useState<boolean>(false);
    const { setLevel } = useProfily();
    const isLegalEntity = profily?.typprihlaseni === 'DLT'; // pravnicka osoba

    const apiListZadostOsobaEdit = (idZadost: number | string, _: number | string, personObject: ISubjPravo) => {
        return {
            downloadSignature: () =>
                zadostPravoPUT(idZadost, personObject).then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        pdfPodpisApi(idZadost!);
                    }
                }),
            pdfBiReroute: () =>
                zadostPravoPUT(idZadost, personObject).then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        return pdfBiRerouteApi(idZadost || 0, language);
                    }
                }),
            biReroute: () =>
                zadostPravoPUT(idZadost, personObject).then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        return biAmlzRerouteApi(idZadost, language);
                    }
                })
        };
    };

    useEffect(() => {
        if (!zadostAgenda?.pristupc2?.idosoby) {
            confirm({
                title: <TranslateText>POTVRDENI</TranslateText>,
                content: <TranslateText>AMLZ_AGENDA_ZADOST_CONFIRM</TranslateText>,
                cancelText: <TranslateText>NE</TranslateText>,
                okText: <TranslateText>ANO</TranslateText>,
                onOk() {
                    setLoading(true);
                    osobyC2Nova()
                        .then(resp => {
                            if (resp.stav === 'ZALOZENO') {
                                setLevel('zadostAgenda', {
                                    pristupc2: {
                                        id: resp.idzadosti,
                                        idosoby: resp.idosoby
                                    }
                                });
                                amlzC2osobyDetailGet(resp.idzadosti, resp.idosoby)
                                    .then(data =>
                                        setZadostData({
                                            ...zadostAgenda?.osobaData,
                                            ...data
                                        })
                                    )
                                    .finally(() => setLoading(false));
                            } else {
                                setLoading(false);
                                NotificationComponent({
                                    title:
                                        getLanguageObject(language)['NELZE_POKRACOVAT_' + resp.stav] +
                                            ' ' +
                                            resp.uzivatel || '',
                                    type: ENotificationType.ERROR
                                });
                                navigateWorkaround(
                                    `/firma/${aktivniFirma?.iducet}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY}`
                                );
                            }
                        })
                        .catch(() => setLoading(false));
                },
                onCancel() {
                    navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY}`);
                }
            });
        } else {
            setLoading(true);
            amlzC2osobyDetailGet(zadostAgenda?.pristupc2?.id!, zadostAgenda?.pristupc2?.idosoby!)
                .then(resp => {
                    if (
                        resp.stav === EStavZadostAmlz.INI ||
                        (resp.stav === EStavZadostAmlz.ROZP &&
                            resp.doplneno &&
                            zadostAgenda.pristupc2?.idprofilu + '' === profily?.id + '')
                    ) {
                        const updateData = {
                            ...resp,
                            stav: EStavZadostAmlz.ROZP
                        };
                        //   zadostAgenda.pristupc2?.idprofilu + '' === profily?.id + '' && delete updateData.doplneno;
                        amlzC2osobyDetailsend(
                            zadostAgenda?.pristupc2?.id!,
                            zadostAgenda?.pristupc2?.idosoby!,
                            updateData,
                            'PUT'
                        );
                        setZadostData(updateData);
                    } else {
                        setZadostData({
                            ...zadostAgenda?.osobaData,
                            ...resp
                        });
                    }
                })
                .finally(() => setLoading(false));
        }
        // zbavim sa osobaData ktore nastavujem v prehlede
        return () =>
            setLevel('zadostAgenda', {
                pristupamlz: zadostAgenda?.pristupamlz,
                pristupc2: zadostAgenda?.pristupc2
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitZadostData) {
            if (!personDataFinish) {
                (setLoading(true),
                amlzC2osobyDetailsend(
                    zadostAgenda?.pristupc2?.id!,
                    zadostAgenda?.pristupc2?.idosoby!,
                    submitZadostData,
                    submitZadostData.stav === EStavZadostAmlz.CDCPKEKONTR ||
                        submitZadostData.stav === EStavZadostAmlz.AUTKONTROLA ||
                        submitZadostData.stav === EStavZadostAmlz.CDCPKONTR ||
                        submitZadostData.stav === EStavZadostAmlz.HOTOVO ||
                        submitZadostData.stav === EStavZadostAmlz.HOTOVOIIS
                        ? 'POST'
                        : 'PUT'
                ))
                    .then(data => {
                        if (
                            (isSubmittingManually || submitZadostData.stav === EStavZadostAmlz.UZIVEMAIL) &&
                            data.stav === 'OK'
                        ) {
                            NotificationComponent({
                                type: ENotificationType.SUCCESS,
                                //   title: 'IDENTIFIKACE_OSOBY',
                                description: 'AMLZ_C2_PUT_SUCCESS'
                            });
                            setIsSubmittingManually(false);
                            navigateWorkaround(
                                `/firma/${aktivniFirma?.iducet}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY}`
                            );
                        }
                        data && setPutResp(data.stav === 'OK' ? '' : data.stav);
                        setZadostData(submitZadostData);
                        amlzC2osobyDetailGet(zadostAgenda?.pristupc2?.id!, zadostAgenda?.pristupc2?.idosoby!)
                            .then(rezp => {
                                setZadostData(rezp);
                                if (showInstrukceAfterFinish) {
                                    setLoading(true);
                                    platebniInstrukcePDFapi(zadostAgenda?.pristupc2?.id!, language).finally(() =>
                                        setLoading(false)
                                    );

                                    setshowInstrukceAfterFinish(false);
                                } else if (personDataFinish) {
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY}`
                                    );
                                }
                            })
                            .finally(() => setLoading(false));
                    })
                    .catch(() => setLoading(false));
            } else {
                amlzC2osobyDetailsend(
                    zadostAgenda?.pristupc2?.id!,
                    zadostAgenda?.pristupc2?.idosoby!,
                    submitZadostData,
                    'POST'
                )
                    .then(() => {
                        NotificationComponent({
                            type: ENotificationType.SUCCESS,
                            //   title: 'IDENTIFIKACE_OSOBY',
                            description: 'ZMENA_UDAJU_SUCESS'
                        });
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY}`);
                    })
                    .finally(() => setLoading(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostAgenda?.pristupc2?.id, submitZadostData]);

    return (
        <LoadingWrapper loading={loading}>
            <Title
                level={3}
                text={
                    isLegalEntity
                        ? 'AMLZ_OSOBY_MENU_LEGALENTITY'
                        : aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                          ? 'EMITENT_PREHLED_TITLE_ADMEMIT'
                          : 'AMLZ_PREHLED_OSOBY'
                }
            />
            {zadostAgenda?.pristupc2?.id && zadostData ? (
                <Row gutter={8}>
                    <Col span={24}>
                        <AgendaAmlz
                            setPdfCheckError={setPutResp}
                            pdfCheckError={putResp}
                            personData={zadostData}
                            setSubmitPersonData={setSubmitZadostData}
                            setPersonDataFinish={setPersonDataFinish}
                            idZadosti={zadostAgenda?.pristupc2?.id}
                            setshowInstrukceAfterFinish={setshowInstrukceAfterFinish}
                            //   putResp={putResp}
                            mode={
                                [
                                    EStavZadostAmlz.HOTOVO,
                                    EStavZadostAmlz.HOTOVOIIS,
                                    EStavZadostAmlz.ZAMITNUTO,
                                    EStavZadostAmlz.ZNEPLATNENO
                                ].includes(zadostData?.stav!)
                                    ? 'displayOnly'
                                    : 'zadostEdit'
                            }
                            apiList={apiListZadostOsobaEdit}
                            setIsSubmittingManually={setIsSubmittingManually}
                            idOsoby={
                                zadostAgenda?.pristupc2?.idprofilu! ||
                                (zadostAgenda?.pristupc2?.isBankIDredirect && profily?.id)
                                    ? Number(profily!.id)
                                    : 0
                            }
                            isAmlzRequired={
                                Number(zadostAgenda?.pristupc2?.idprofilu!) === Number(profily?.id) ||
                                zadostAgenda?.pristupc2?.isBankIDredirect
                            }
                        />
                    </Col>
                </Row>
            ) : (
                <></>
            )}
        </LoadingWrapper>
    );
};
export default OsobyZastupujiciEmitenta;
